'use client'

import { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { flagshipAccentAt, FLAGSHIP_ACCENT_VAR } from '@/lib/flagship-colors'

interface ProcessStep {
  number: string
  title: string
  description: string
}

export default function HomeProcess() {
  const t = useTranslations('homepage.process')
  const steps = t.raw('steps') as ProcessStep[]
  const sectionRef = useRef<HTMLElement>(null)
  const trackRef = useRef<HTMLOListElement>(null)

  // Desktop: the section is as tall as the sideways travel, its viewport sticks to the top, and
  // vertical scroll progress slides the step track horizontally (1 px of scroll = 1 px sideways).
  useEffect(() => {
    const section = sectionRef.current
    const track = trackRef.current
    if (!section || !track) return
    const desktop = window.matchMedia('(min-width: 1024px)')
    let frame = 0
    let lastTravel = -1

    const update = () => {
      frame = 0
      if (!desktop.matches) {
        section.style.height = ''
        track.style.transform = ''
        lastTravel = -1
        return
      }
      const travel = Math.max(
        0,
        track.scrollWidth - (track.parentElement?.clientWidth ?? window.innerWidth)
      )
      section.style.height = `${window.innerHeight + travel}px`
      const progress = Math.min(
        1,
        Math.max(0, -section.getBoundingClientRect().top / (travel || 1))
      )
      track.style.transform = `translate3d(${-progress * travel}px, 0, 0)`
      if (travel !== lastTravel) {
        lastTravel = travel
        // page height changed: let the globe re-measure where each section sits
        window.dispatchEvent(new Event('resize'))
      }
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    schedule()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  // Desktop: steps sit in a scroll-driven sideways track, so keyboard focus has to scroll the
  // page to the point where the focused step is centred instead of relying on scrollIntoView.
  const revealStep = (index: number) => {
    const section = sectionRef.current
    const track = trackRef.current
    const viewport = track?.parentElement
    if (!section || !track || !viewport || !window.matchMedia('(min-width: 1024px)').matches) return
    viewport.scrollTo({ left: 0 })
    const travel = Math.max(0, track.scrollWidth - viewport.clientWidth)
    const sectionTop = section.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: sectionTop + (travel * index) / Math.max(1, steps.length - 1) })
  }

  return (
    <section
      aria-labelledby="process-heading"
      ref={sectionRef}
      className="home-snap relative px-6 py-10 sm:px-10 sm:py-12 lg:block lg:p-0"
    >
      <div className="mx-auto w-full max-w-5xl lg:sticky lg:top-0 lg:flex lg:h-dvh lg:max-w-none lg:flex-col lg:justify-center lg:overflow-hidden">
        <div className="md:text-center lg:px-16">
          <h2
            tabIndex={0}
            id="process-heading"
            className="block-focus [font-family:var(--font-display)] text-2xl text-[var(--flagship-text)] sm:text-4xl md:text-5xl lg:text-4xl"
          >
            {t('heading')}
          </h2>
          <p
            tabIndex={0}
            className="block-focus mt-3 max-w-2xl text-base text-[var(--flagship-muted)] sm:mt-4 sm:text-lg md:mx-auto md:text-xl lg:text-lg"
          >
            {t('intro')}
          </p>
        </div>
        <ol
          ref={trackRef}
          className="mt-8 space-y-6 border-l-2 border-dashed border-[var(--flagship-muted)]/30 pl-6 will-change-transform sm:mt-10 sm:space-y-8 sm:pl-8 md:mt-12 md:grid md:grid-cols-3 md:gap-5 md:space-y-0 md:border-0 md:pl-0 lg:mt-14 lg:flex lg:w-max lg:gap-10 lg:space-y-0 lg:border-0 lg:px-[31vw]"
        >
          {steps.map((step, index) => {
            const accent = FLAGSHIP_ACCENT_VAR[flagshipAccentAt(index)]
            return (
              <li
                key={step.number}
                tabIndex={0}
                onFocus={() => revealStep(index)}
                className="block-focus group relative flex items-center overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 rounded-2xl border border-white/25 bg-[rgba(24,18,52,0.55)] p-5 sm:p-6 md:items-start lg:w-[38vw] lg:items-center lg:rounded-[2rem] lg:border lg:p-12 lg:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.5)]"
                style={
                  {
                    '--accent': accent,
                    boxShadow:
                      'inset 0 1px 0 0 rgba(255,255,255,0.35), inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 60px -30px var(--accent), 0 20px 60px -20px color-mix(in srgb, var(--accent) 35%, transparent)',
                  } as React.CSSProperties
                }
              >
                {/* specular top sheen */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/[0.10] to-transparent lg:h-32" />
                {/* accent glow bleeding in from the border */}
                <div
                  className="pointer-events-none absolute -inset-px rounded-[inherit] opacity-40 transition-opacity duration-500 group-hover:opacity-70"
                  style={{
                    background:
                      'radial-gradient(120% 100% at 0% 0%, color-mix(in srgb, var(--accent) 22%, transparent), transparent 60%)',
                  }}
                />
                {/* shimmer sweep on hover */}
                <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full" />

                <div className="relative z-10">
                  <h3 className="[font-family:var(--font-ui)] text-lg font-bold text-[var(--flagship-text)] sm:text-xl md:text-2xl lg:text-4xl">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-[var(--flagship-muted)] sm:mt-2 sm:text-base lg:mt-4 lg:text-xl">
                    {step.description}
                  </p>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
